import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { authFunctionMiddleware } from "~/server/middleware/auth";

/**
 * Detects whether the signed-in user has an email/password credential account.
 * OAuth-only users (Google / Microsoft) will not have one until they set a password.
 */
export const getHasPassword = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async ({ context }) => {
    const { db } = await import("~/lib/db");
    const { account } = await import("~/schemas/auth-schema");
    const { and, eq } = await import("drizzle-orm");

    const rows = await db
      .select({ id: account.id })
      .from(account)
      .where(
        and(
          eq(account.userId, context.user.id),
          eq(account.providerId, "credential"),
        ),
      )
      .limit(1);

    return rows.length > 0;
  });

const SetPasswordInputSchema = z.object({
  newPassword: z.string().min(8),
});

/**
 * Sets or replaces the password for the signed-in user without requiring the
 * current password — the session itself proves identity (user is already
 * logged in). Creates the credential account if the user is OAuth-only.
 * Must run server-side.
 */
export const setUserPassword = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => SetPasswordInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    const { auth } = await import("~/lib/auth");
    const { getRequest } = await import("@tanstack/solid-start/server");

    const request = getRequest();
    const authContext = await auth.$context;

    // Verify a credential account exists; create one if the user is OAuth-only.
    const accounts = await authContext.internalAdapter.findAccounts(
      context.user.id,
    );
    const credential = accounts.find((a) => a.providerId === "credential");

    const passwordHash = await authContext.password.hash(data.newPassword);

    if (credential) {
      await authContext.internalAdapter.updateAccount(credential.id, {
        password: passwordHash,
      });
    } else {
      await authContext.internalAdapter.linkAccount({
        userId: context.user.id,
        providerId: "credential",
        accountId: context.user.id,
        password: passwordHash,
      });
    }

    // Revoke all other sessions (keep the current one active).
    await auth.api.revokeSessions({ headers: request.headers });

    return { status: true };
  });
